import { useEffect, useState } from "react";
import { Loader2, Plus, Pencil, Trash2, BedDouble, ShoppingBag } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function RoomsPage() {
  const [rooms, setRooms] = useState([]);
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ label: "", notes: "" });
  const [saving, setSaving] = useState(false);

  const load = () => Promise.all([
    api.get("/rooms").then((r) => setRooms(r.data)),
    api.get("/rooms/orders-summary").then((r) => setSummary(r.data)),
  ]).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.label.trim()) return toast.error("Enter a room label");
    setSaving(true);
    try {
      if (editing === "new") await api.post("/rooms", form);
      else await api.put(`/rooms/${editing}`, form);
      toast.success("Room saved"); setEditing(null); load();
    } catch { toast.error("Could not save"); } finally { setSaving(false); }
  };
  const remove = async (id) => { await api.delete(`/rooms/${id}`); toast.success("Room removed"); load(); };
  const totalFor = (label) => summary.find((s) => s.room === label);

  if (loading) return <Loader2 className="w-5 h-5 animate-spin text-accent" />;

  return (
    <div className="space-y-8" data-testid="rooms-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl text-foreground mb-1">Rooms</h1>
          <p className="text-muted-foreground text-sm">Label your rooms, then assign guests to them so orders are tracked per room.</p>
        </div>
        <Button onClick={() => { setEditing("new"); setForm({ label: "", notes: "" }); }} data-testid="add-room-btn" className="bg-primary text-primary-foreground gap-1.5 shrink-0"><Plus className="w-4 h-4" /> Add room</Button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="rooms-list">
        {rooms.length === 0 ? (
          <div className="col-span-full border border-border rounded-md bg-card text-center py-14"><BedDouble className="w-7 h-7 text-muted-foreground mx-auto mb-2" /><p className="text-sm text-muted-foreground">No rooms yet. Add 101, 102, Suite A…</p></div>
        ) : rooms.map((r) => {
          const t = totalFor(r.label);
          return (
            <div key={r.id} className="border border-border rounded-md bg-card p-5" data-testid={`room-${r.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2"><BedDouble className="w-5 h-5 text-accent" /><span className="font-serif text-xl text-foreground">{r.label}</span></div>
                <div className="flex gap-1">
                  <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => { setEditing(r.id); setForm({ label: r.label, notes: r.notes || "" }); }} data-testid={`edit-room-${r.id}`}><Pencil className="w-4 h-4" /></Button>
                  <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600" onClick={() => remove(r.id)} data-testid={`delete-room-${r.id}`}><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
              {r.notes && <p className="text-sm text-muted-foreground mt-1">{r.notes}</p>}
              <div className="mt-3 flex items-center gap-1.5 text-sm text-muted-foreground"><ShoppingBag className="w-4 h-4" /> {t ? `${t.orders} order(s) · £${t.total.toFixed(2)}` : "No orders yet"}</div>
            </div>
          );
        })}
      </div>

      {summary.some((s) => s.room === "Unassigned") && (
        <p className="text-xs text-muted-foreground">Some orders are not tied to a room. Assign a room when checking guests in to track them here.</p>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="room-dialog">
          <DialogHeader><DialogTitle className="font-serif text-xl">{editing === "new" ? "Add room" : "Edit room"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Room label</Label><Input data-testid="room-label" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="101 / Suite A" className="mt-1.5" /></div>
            <div><Label>Notes (optional)</Label><Input data-testid="room-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Sea view, king bed" className="mt-1.5" /></div>
            <Button onClick={save} disabled={saving} data-testid="save-room-btn" className="w-full bg-primary text-primary-foreground">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save room"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
